import type { HttpError } from '@refinedev/core'
import { useShow } from '@refinedev/core'
import type { Tenant } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import { ShowView, ShowViewHeader } from '~/components/refineUi/views/showView'
import { formatDateTime } from '~/i18n/format'
import { History } from '~/pages/activity/history'
import { useGameOptions } from '~/pages/cabinets/useGameOptions'
import { NotFoundPage } from '~/pages/notFound'
import { OpenTenantButton, TenantStatusBadge } from './tenantParts'

export function TenantShow() {
  const { t } = useTranslation()
  const { query } = useShow<Tenant, HttpError>({ resource: 'tenants' })
  const tenant = query.data?.data
  // its default configurations name games by id in the history
  const { names: gameNames } = useGameOptions()

  if (query.error?.statusCode === 404) {
    return <NotFoundPage />
  }

  return (
    <ShowView>
      <ShowViewHeader
        title={tenant?.name}
        actionsSlot={
          tenant && (
            <OpenTenantButton
              tenant={tenant}
              kind="openTenant"
            />
          )
        }
      />
      {query.error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('tenantsPage.common.loadError', { message: query.error.message })}
        </p>
      )}
      {tenant && (
        <div className="space-y-6">
          <dl className="grid max-w-md grid-cols-[8rem_1fr] gap-2 text-sm">
            <dt className="text-muted-foreground">
              {t('tenantsPage.common.name')}
            </dt>
            <dd>{tenant.name}</dd>
            <dt className="text-muted-foreground">
              {t('tenantsPage.common.slug')}
            </dt>
            <dd>{tenant.slug}</dd>
            <dt className="text-muted-foreground">
              {t('tenantsPage.common.status')}
            </dt>
            <dd>
              <TenantStatusBadge status={tenant.status} />
            </dd>
            <dt className="text-muted-foreground">
              {t('tenantsPage.common.created')}
            </dt>
            <dd>{formatDateTime(tenant.createdAt)}</dd>
            <dt className="text-muted-foreground">
              {t('tenantsPage.common.updated')}
            </dt>
            <dd>{formatDateTime(tenant.updatedAt)}</dd>
          </dl>
          {/* a tenant's history is its own log, also on another tenant's host */}
          <History
            record={{ entity: 'tenant', id: tenant.id, tenantId: tenant.id }}
            tenantSlug={tenant.slug}
            gameNames={gameNames}
          />
        </div>
      )}
    </ShowView>
  )
}
